import { theme } from '@kijiji/theme'
import { useMemo, useRef } from 'react'

import { ShowAtOrLarger } from '@/components/shared/breakpoint'
import { FiltersDrawer } from '@/components/srp/filters/FiltersDrawer/FiltersDrawer'
import { ALL_CATEGORIES_ID_NUM } from '@/constants/category'
import { FILTER_CANONICAL } from '@/constants/search'
import { getExpandedFilterIdList } from '@/domain/srp/filters/getExpandedFilterIdList'
import { AllFiltersButton } from '@/features/filters/components/QuickFilters/AllFiltersButton'
import { QuickFilterButton } from '@/features/filters/components/QuickFilters/QuickFilterButton'
import { QuickFiltersLoading } from '@/features/filters/components/QuickFilters/QuickFiltersLoading'
import {
  QuickFiltersContainer,
  QuickFiltersScrollContainer,
} from '@/features/filters/components/QuickFilters/styled'
import { getQuickFilters } from '@/features/filters/utils/getQuickFilters'
import { useGetSearchResultsData } from '@/hooks/srp'
import { useFilterDrawerState } from '@/hooks/srp/useFilterDrawerState'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { trackEvent } from '@/lib/ga/utils/tracking'
import { Divider } from '@/ui/atoms/divider'
import { Spacing } from '@/ui/atoms/spacing'

export const QuickFilters = ({
  isMobile,
  isQuickFilterReExperimentActive = false,
  isSrpControlsMobileToggleEnabled = false,
}: {
  isMobile?: boolean
  isQuickFilterReExperimentActive?: boolean
  isSrpControlsMobileToggleEnabled?: boolean
}) => {
  const { data, loadingFilters } = useGetSearchResultsData()
  const { controls, searchQuery } = data ?? {}
  const quickFiltersSorted = useMemo(() => {
    const filters = controls?.filtering ?? []
    return getQuickFilters(filters)
  }, [controls?.filtering])
  const { spacing } = theme

  const selectedCategoryId = searchQuery?.category?.id ?? ALL_CATEGORIES_ID_NUM
  const accordionContainerRef = useRef<HTMLDivElement>(null)
  const { setFilterDrawerState } = useFilterDrawerState()

  const resetScrollPosition = () => {
    accordionContainerRef.current?.scrollTo(0, 0)
  }

  const scrollToFilter = (id: string) => {
    const filterElement = accordionContainerRef.current?.querySelector(
      `[data-accordion-id="${id}"]`
    )
    if (filterElement) {
      filterElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  const handleClickAllFilters = () => {
    trackEvent({ action: GA_EVENT.QuickFilterClick, label: 'filter=all' })
    setFilterDrawerState({
      isOpen: true,
      openItems: isMobile ? [] : getExpandedFilterIdList(selectedCategoryId),
    })

    resetScrollPosition()
  }

  const handleClickQuickFilter = (id: string) => {
    // The category section is a special case, it uses the filter group name as its accordion id
    if (id === FILTER_CANONICAL.CATEGORY) id = FILTER_CANONICAL.CATEGORY_SECTION

    trackEvent({ action: GA_EVENT.QuickFilterClick, label: `filter=${id}` })
    setFilterDrawerState({
      isOpen: true,
      openItems: [id],
    })
    scrollToFilter(id)
  }

  const renderQuickFilterButtons = () => {
    return loadingFilters || !quickFiltersSorted?.length ? (
      <QuickFiltersLoading />
    ) : (
      quickFiltersSorted.map((filter) => (
        <QuickFilterButton
          key={filter.name}
          filter={filter}
          onClick={() => {
            handleClickQuickFilter(filter.name)
          }}
        />
      ))
    )
  }

  return (
    <QuickFiltersContainer
      isQuickFilterReExperimentActive={isQuickFilterReExperimentActive}
      isSrpControlsMobileToggleEnabled={isSrpControlsMobileToggleEnabled}
    >
      <FiltersDrawer accordionContainerRef={accordionContainerRef} />
      <Spacing pTop={spacing.xLarge}>
        <QuickFiltersScrollContainer gap={spacing.defaultSmall}>
          {isSrpControlsMobileToggleEnabled && isMobile ? (
            <>
              <AllFiltersButton isSrpControlsMobileToggleEnabled onClick={handleClickAllFilters} />
              {renderQuickFilterButtons()}
            </>
          ) : (
            <>
              {renderQuickFilterButtons()}
              <AllFiltersButton onClick={handleClickAllFilters} />
            </>
          )}
        </QuickFiltersScrollContainer>
      </Spacing>
      <ShowAtOrLarger breakpoint="medium">
        <Divider bottom={spacing.default} top={spacing.default} />
      </ShowAtOrLarger>
    </QuickFiltersContainer>
  )
}
